import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { ICONS } from 'components/Icon';
import { replace } from 'components/Navigation/navigationRef';
import {
  ModalScreenProps,
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { ScreenSize } from 'lib/constants';
import { calculateAutoId, displayAutoId } from 'lib/fields';
import { dateToNumber, filterArrayEmpty, randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { cloneDeep } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { GlobalFieldRepeatableInstanceScreen } from 'screens/FieldComponents/GlobalFieldRepeatableInstanceScreen';
import { NAVIGATE_MODAL, NAVIGATE_MODAL_BACK } from 'store/actions';
import {
  doAseFieldInstanceDelete,
  doAseFieldInstanceDuplicate,
  doAseFieldInstanceSave,
  doAseFieldInstanceUnlink,
  doAseLoadElementPdf,
} from 'store/assessments/actions';
import {
  selectAseFieldInstanceAse,
  selectAseFieldInstanceAst,
  selectAseFieldInstanceNextDraftAutoIdForGroup,
  selectAseFieldInstanceNextAutoIdForGroup,
  selectAseAssetId,
  selectAseFieldInstancesForGroup,
  selectAseTplFieldGroup,
  selectAseTemplateFieldFieldsConfig,
  selectAseIsEditable,
  selectAseIsApproved,
  selectAseId,
  selectAseIsOnline,
  selectAseFieldInstanceChildInstanceIsInParentAstAse,
  selectAseTplFieldToGroup,
  selectAseTemplateSettings,
  selectAseDataFieldFields,
  selectAseExplorerOptionShowValidationErrors,
} from 'store/assessments/selectors';
import { FieldTypes } from 'store/assets/types';
import { PinNew } from 'store/plan/types';
import {
  AppContextEnum,
  FieldInstanceWithFormattedAutoId,
  FieldResult,
} from 'types/types';
import { v4 as uuid } from 'uuid';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { str } from 'lib/str';
import { AddCustomAction } from '../Explorer/ExplorerActionRow';
import { AddCustomControl } from '../Explorer/ExplorerControlRow';
import { BelongsToField } from './BelongsToField';

// Keep the parent screen's type so we can use it's navigation
type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASEREPEATABLE],
  Screens.ASEREPEATABLE_INSTANCE
>;

export const RepeatableInstanceScreenForNavigation: React.FC<Props> = ({
  navigation,
  route,
}) => <RepeatableInstanceScreen route={route} navigation={navigation} />;

export const RepeatableInstanceScreen: React.FC<
  | ModalScreenProps<ModalScreens.AseFieldInstance>
  | {
      route: Pick<Props['route'], 'params'>;
      navigation: {
        goBack: Props['navigation']['goBack'];
        setOptions: Props['navigation']['setOptions'];
      };
      isModal?: undefined;
    }
> = ({ navigation, route, isModal }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();
  const isDirty = React.useRef(false);

  const {
    groupId,
    instanceId,
    initValues,
    isAddingExisting,
    parent,
    initResults,
    isEditable: incomingIsEditable = true,
  } = route.params;

  const internalSave = !!isModal;

  const initResultsSetRef = React.useRef(false);
  const assessmentId = useSelector(selectAseId);
  const isOnline = useSelector(selectAseIsOnline);
  const isApproved = useSelector(selectAseIsApproved);
  const isEditable = useSelector(selectAseIsEditable) && incomingIsEditable;
  const assetId = useSelector(selectAseAssetId);
  const templateSettings = useSelector(selectAseTemplateSettings);
  const showValidationErrors = useSelector(
    selectAseExplorerOptionShowValidationErrors,
  );

  // Select group and fields info
  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));
  const fields = useSelector(selectAseDataFieldFields);
  const parentGroup = useSelector((state) =>
    selectAseTplFieldToGroup(state, parent?.fieldId || '-1'),
  );
  const fieldConfigs = useSelector(selectAseTemplateFieldFieldsConfig);

  // Select instances
  const assessmentInstance = useSelector((state) =>
    selectAseFieldInstanceAse(state, instanceId || '-1'),
  );
  const assetInstance = useSelector((state) =>
    selectAseFieldInstanceAst(state, instanceId || '-1'),
  );
  const isInParentAstAse = useSelector((state) =>
    selectAseFieldInstanceChildInstanceIsInParentAstAse(
      state,
      parent,
      instanceId,
    ),
  );
  const allInstances = useSelector((state) =>
    selectAseFieldInstancesForGroup(state, {
      groupId,
      parent: null,
      inAssessmentOnly: false,
    }),
  );
  const nextAutoId = useSelector((state) =>
    selectAseFieldInstanceNextAutoIdForGroup(state, groupId),
  );
  const autoIdDraftNext = useSelector((state) =>
    selectAseFieldInstanceNextDraftAutoIdForGroup(state, groupId),
  );

  const getSelectedInstance = React.useCallback(() => {
    return assetInstance && assessmentInstance
      ? {
          ...assetInstance,
          updatedTs: assessmentInstance.updatedTs,
          results: {
            ...assetInstance.results,
            ...assessmentInstance.results,
          },
          action: assessmentInstance.action,
        }
      : assetInstance
        ? { ...assetInstance }
        : assessmentInstance
          ? { ...assessmentInstance }
          : {
              instanceId: uuid(),
              reference: '',
              groupId,
              autoId: 0,
              draftAutoId: autoIdDraftNext,
              createdTs: dateToNumber(),
              updatedTs: dateToNumber(),
              results: initResults || {},
              action: null,
              inAssessment: true,
              formattedAutoId: displayAutoId(
                calculateAutoId(undefined, autoIdDraftNext, nextAutoId),
                group?.autoIdZeroFill,
                group?.autoIdPrefix,
              ),
            };
  }, [
    assessmentInstance,
    assetInstance,
    autoIdDraftNext,
    group?.autoIdPrefix,
    group?.autoIdZeroFill,
    groupId,
    nextAutoId,
    initResults,
  ]);

  const instance = React.useRef<FieldInstanceWithFormattedAutoId>(
    cloneDeep(getSelectedInstance()),
  );

  if (!internalSave) {
    instance.current = cloneDeep(getSelectedInstance());
  }

  const resultsAssetRef = React.useRef({ ...(assetInstance?.results || {}) });
  const resultsAssessmentRef = React.useRef({
    ...(assessmentInstance?.results || {}),
  });

  const dirtyResultsRef = React.useRef<Record<string, FieldResult>>({});

  if (initResults && !initResultsSetRef.current) {
    initResultsSetRef.current = true;
    dirtyResultsRef.current = initResults;
  }

  const checkIsDirty = () => !!Object.keys(dirtyResultsRef.current).length;
  isDirty.current = !!isModal && checkIsDirty();

  const otherInstances = React.useMemo(
    () =>
      instanceId
        ? Object.values(allInstances).filter(
            ({ instanceId: otherInstanceId }) => otherInstanceId !== instanceId,
          )
        : Object.values(allInstances),
    [instanceId, allInstances],
  );

  const fieldKeys = React.useRef(
    (group?.fieldIds || []).reduce<Record<string, string>>((acc, fieldId) => {
      acc[fieldId] = `${fieldId}-${randomKey()}`;
      return acc;
    }, {}),
  );

  const actuallyGoBack = React.useCallback(() => {
    if (isModal) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === ScreenSize.large) {
      replace(Stacks.ASEREPEATABLE, Screens.ASEREPEATABLE_INTRO, {
        groupId,
        parent,
      });
    } else if (navigation) {
      navigation.goBack();
    }
  }, [dispatch, groupId, isModal, navigation, parent, screenSize]);

  const handleGoBack = () => {
    if (isModal && isDirty.current) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: actuallyGoBack,
      });
      return;
    }
    actuallyGoBack();
  };

  const handleSave = React.useCallback(() => {
    dispatch(
      doAseFieldInstanceSave({
        instance: {
          ...instance.current,
          results: { ...dirtyResultsRef.current },
        },
        parent,
        isAdding: isAddingExisting || !instanceId,
        isLite: false,
        newLiteInstances: {},
      }),
    );
    if (!internalSave) {
      dirtyResultsRef.current = {};
    }
  }, [dispatch, instanceId, internalSave, isAddingExisting, parent]);

  const handleChange = React.useCallback(
    (
      fieldId: string,
      value: string | string[] | null,
      pin?: PinNew | '' | null,
    ) => {
      const field = fields[fieldId];
      if (!field) {
        return;
      }
      let newResult: FieldResult | undefined;
      let removeResult: boolean = false;
      if (field.type === FieldTypes.PIN) {
        if (pin === null) {
          // Null pins remove the assessment's value and revert to the asset's value
          delete instance.current.results[fieldId];
          delete resultsAssessmentRef.current[fieldId];
          newResult = assetInstance?.results[fieldId];
          removeResult = true;
        } else {
          newResult = {
            fieldId,
            ts: dateToNumber(),
            value: '',
            pin: pin || '',
            action: null,
          };
          resultsAssessmentRef.current = {
            ...resultsAssessmentRef.current,
            [fieldId]: { ...newResult },
          };
        }
      } else if (value === null) {
        // Null values remove the assessment's value and revert to the asset's value
        delete instance.current.results[fieldId];
        delete resultsAssessmentRef.current[fieldId];
        newResult = assetInstance?.results[fieldId];
        removeResult = true;
      } else {
        newResult = {
          fieldId,
          ts: dateToNumber(),
          value: value || value === '0' ? value : '',
          pin: pin || '',
          action: null,
        };
        resultsAssessmentRef.current = {
          ...resultsAssessmentRef.current,
          [fieldId]: { ...newResult },
        };
      }
      if (newResult) {
        instance.current = {
          ...instance.current,
          results: {
            ...instance.current.results,
            [fieldId]: { ...newResult },
          },
        };
      }
      dirtyResultsRef.current = {
        ...dirtyResultsRef.current,
        [fieldId]: {
          fieldId,
          value: value !== null ? value : '',
          pin: pin || '',
          ts: dateToNumber(),
          action: removeResult ? 'delete' : null,
        },
      };
      instance.current.updatedTs = dateToNumber();
      if (isModal) {
        // Store locally and save on onSavePress
        forceUpdate();
      } else {
        // Save right away
        handleSave();
      }
    },
    [assetInstance?.results, forceUpdate, fields, handleSave, isModal],
  );

  const handleRevert = React.useCallback(
    (fieldId: string) => {
      confirm({
        text: "This field has been changed in this assessment. Continuing will revert the value back to the asset's value",
        confirmText: 'Revert',
        destructive: true,
        onConfirm: () => {
          fieldKeys.current[fieldId] = `${fieldId}-${randomKey()}`;
          handleChange(fieldId, null, null);
        },
      });
    },
    [confirm, handleChange],
  );

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(isModal && handleGoBack);

  React.useEffect(() => {
    if (initValues) {
      const newResults = Object.entries(initValues).reduce<
        FieldInstanceWithFormattedAutoId['results']
      >(
        (acc, [initFieldId, initValue]) => {
          acc[initFieldId] = {
            fieldId: initFieldId,
            ts: dateToNumber(),
            value: initValue,
            pin: '',
            action: null,
          };
          return acc;
        },
        { ...instance.current.results },
      );
      instance.current = {
        ...instance.current,
        results: { ...instance.current.results, ...newResults },
      };
      dirtyResultsRef.current = {
        ...dirtyResultsRef.current,
        ...newResults,
      };
      forceUpdate();
    }
  }, [forceUpdate, initValues]);

  const isBeingOpenInvalid = isAddingExisting
    ? !assessmentInstance && !assetInstance
    : instanceId && (!assessmentInstance || (parent && !isInParentAstAse));
  React.useEffect(() => {
    if (isBeingOpenInvalid) {
      actuallyGoBack();
    }
  }, [actuallyGoBack, isBeingOpenInvalid]);

  if (!group) {
    return null;
  }

  const singular = group.calculatedInfo.singularName.toLowerCase();

  const postPromptText = assetInstance
    ? 'Changes will appear on the asset after the assessment is approved'
    : 'It will appear on the asset after the assessment is approved';

  const prompt = isApproved
    ? ''
    : assetInstance && !assessmentInstance
      ? `After saving, this existing ${singular} will be included in this assessment. ${postPromptText}`
      : !assetInstance && !assessmentInstance
        ? `After saving, this new ${singular} will be added to this assessment. ${postPromptText}`
        : assetInstance
          ? `This ${singular} has been included in this assessment. ${postPromptText}`
          : `This ${singular} was created in this assessment. ${postPromptText}`;

  const promptTitle = isApproved ? '' : assetInstance ? 'Existing' : 'New';

  const { editableFieldIds, requiredFieldIds, hiddenFieldIds } =
    group.fieldIds.reduce(
      (acc, fieldId) => {
        const fieldConfig = fieldConfigs[fieldId];
        const field = fields[fieldId];
        if (!fieldConfig || !field) {
          acc.hiddenFieldIds.push(fieldId);
          return acc;
        }
        if (
          isEditable &&
          fieldConfig.edit &&
          (!isOnline ||
            (isModal &&
              ![
                FieldTypes.IMAGE,
                FieldTypes.FILE,
                FieldTypes.PIN,
                FieldTypes.GROUP,
                FieldTypes.GROUPLIGHT,
              ].includes(field.type)))
        ) {
          acc.editableFieldIds.push(fieldId);
        }
        if (fieldConfig.require) {
          acc.requiredFieldIds.push(fieldId);
        }
        return acc;
      },
      {
        editableFieldIds: [] as string[],
        requiredFieldIds: [] as string[],
        hiddenFieldIds: [] as string[],
      },
    );

  const isNew = !assetInstance && !assessmentInstance;
  const isAdd = parent ? !isInParentAstAse : !assessmentInstance;

  return (
    <GlobalFieldRepeatableInstanceScreen
      context={{
        context: isOnline
          ? AppContextEnum.remoteAssessment
          : AppContextEnum.localAssessment,
        assetId,
        assessmentId,
        isModal,
      }}
      id={instance.current.instanceId}
      reference={instance.current.reference}
      promptTitle={promptTitle}
      prompt={prompt}
      autoId={calculateAutoId(
        instance.current.autoId,
        instance.current.draftAutoId,
        nextAutoId,
      )}
      isAdd={isAdd}
      isNew={isNew}
      // createdTs={instance.current.created_ts}
      results={instance.current.results}
      resultsOld={assetInstance ? resultsAssetRef.current : undefined}
      resultsNew={assetInstance ? resultsAssessmentRef.current : undefined}
      group={group}
      fields={fields}
      editableFieldIds={editableFieldIds}
      requiredFieldIds={requiredFieldIds}
      hiddenFieldIds={hiddenFieldIds}
      showInvalid={showValidationErrors}
      autoIdNext={nextAutoId}
      isInModal={!!isModal || !!parent}
      otherInstances={otherInstances}
      onPressSaveButton={
        isEditable
          ? isModal
            ? handleSave
            : isOnline
              ? () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.AseFieldInstance,
                      params: {
                        instanceId: instance.current.instanceId,
                        groupId,
                        parent,
                      },
                    }),
                  )
              : undefined
          : undefined
      }
      saveButtonText={isModal ? 'Save' : 'Edit'}
      cancelButtonText={isEditable ? 'Cancel' : 'Close'}
      onPressCancelButton={isModal ? handleGoBack : undefined}
      onPressBackButton={
        !isModal && screenSize === ScreenSize.small ? handleGoBack : undefined
      }
      fieldKeys={fieldKeys.current}
      onChange={handleChange}
      onRevert={handleRevert}
      headerMenuRight={
        !isAdd &&
        !isNew &&
        assessmentInstance &&
        (isOnline
          ? isEditable || group.allowPdfDownload
          : group.allowPdfDownload)
          ? {
              iconButtonConfig: { icon: ICONS.EllipsisLight },
              options: [
                isEditable
                  ? {
                      text: `Duplicate ${group.calculatedInfo.singularName}`,
                      onPress: () =>
                        dispatch(
                          doAseFieldInstanceDuplicate({
                            instanceId: assessmentInstance.instanceId,
                            parent,
                          }),
                        ),
                      icon: ICONS.CopyLight,
                    }
                  : undefined,
                isEditable
                  ? {
                      text: assetInstance
                        ? 'Remove From Assessment'
                        : 'Delete From RiskBase',
                      icon: assetInstance
                        ? ICONS.TrashCanLight
                        : ICONS.TrashCanXmarkLight,
                      destructive: true,
                      onPress: () =>
                        dispatch(
                          doAseFieldInstanceDelete({
                            instanceId: assessmentInstance.instanceId,
                          }),
                        ),
                    }
                  : undefined,
                isEditable && parent && parentGroup
                  ? {
                      text: `Unlink From ${parentGroup.calculatedInfo.singularName}`,
                      icon: ICONS.LinkHorizontalSlashLight,
                      onPress: () =>
                        dispatch(
                          doAseFieldInstanceUnlink({
                            instanceId: assessmentInstance.instanceId,
                            parent,
                          }),
                        ),
                      destructive: true,
                    }
                  : undefined,
                '-',
                group.allowPdfDownload
                  ? {
                      text: 'Preview Element PDF',
                      onPress: () =>
                        dispatch(
                          doAseLoadElementPdf({
                            elementId: assessmentInstance.instanceId,
                          }),
                        ),
                      icon: ICONS.FileDuotone,
                    }
                  : undefined,
                group.allowPdfDownload
                  ? {
                      text: str.sharedownload('Element PDF'),
                      onPress: () =>
                        dispatch(
                          doAseLoadElementPdf({
                            elementId: assessmentInstance.instanceId,
                            doShare: true,
                          }),
                        ),
                      icon: ICONS.ShareOS,
                    }
                  : undefined,
              ].filter(filterArrayEmpty),
            }
          : undefined
      }
      ComponentAfterReference={
        !!instanceId && (
          <BelongsToField instanceId={instanceId} parent={parent} />
        )
      }
      ComponentAfterFields={
        isEditable &&
        !isModal &&
        !isNew &&
        !isAdd &&
        assessmentInstance &&
        (templateSettings.addCustomActions ||
          templateSettings.addCustomControls) ? (
          <>
            <Card
              label={`Add ${[
                templateSettings.addCustomActions ? 'Actions' : null,
                templateSettings.addCustomControls ? 'Controls' : null,
              ]
                .filter(filterArrayEmpty)
                .join(' and ')}`}
              margin={false}
            />
            <ViewWithSeparators
              separatorMargin={theme.paddingHorizontalHalf / 2}
              separatorColor="transparent"
            >
              {templateSettings.addCustomActions && (
                <Card margin={false}>
                  <AddCustomAction
                    parentId="1"
                    parentType="section"
                    fieldInstanceId={assessmentInstance.instanceId}
                    parent={null}
                  />
                </Card>
              )}
              {templateSettings.addCustomControls && (
                <Card margin={false}>
                  <AddCustomControl
                    parentId="1"
                    parentType="section"
                    fieldInstanceId={assessmentInstance.instanceId}
                    parent={null}
                  />
                </Card>
              )}
            </ViewWithSeparators>
          </>
        ) : undefined
      }
    />
  );
};
